/* iter-53n — Chapter Bible "fill the gaps" popup.
 *
 * Surfaces character bible fields the synopsis import couldn't infer
 * (description, voice, archetype, age, traits) and asks the author to
 * fill them in before drafting. Saves each character via the existing
 * /api/characters/{id} PUT endpoint.
 *
 * iter-coach-bible — Two new affordances per field/character:
 *   1. ✦ Ask Claude — per-gap sparkle button calls
 *      POST /api/projects/characters/{cid}/suggest-field and pre-fills
 *      the input. Author accepts, edits, or ignores.
 *   2. ↗ Find in prose — calls GET /api/projects/{pid}/chapters/find-character
 *      and fires loom:highlight-insertion-point so the editor jumps to the
 *      character's first appearance, then closes the dialog so the author
 *      can write the detail in context.
 *
 * Usage:
 *   <ChapterBibleDialog
 *     open={open}
 *     onOpenChange={setOpen}
 *     projectId={pid}
 *     onSaved={() => refresh()} />
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { BookOpenText, CheckCircle2, ExternalLink, Loader2, Sparkles } from 'lucide-react';
import { AiLoadingOverlay } from '../ui/AiLoadingOverlay';
import { toast } from 'sonner';
import { charactersApi } from '@/lib/api';
import { cn } from '@/lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FIELD_LABELS = {
  role:        'Role',
  description: 'Description (1-2 sentences)',
  voice:       'Voice / speech style',
  archetype:   'Archetype',
  age:         'Age',
  traits:      'Core traits (comma-separated; e.g. courageous, anxious, witty)',
};

const FIELD_PLACEHOLDERS = {
  role:        'protagonist | antagonist | ally | foil | mentor | supporting',
  description: 'A weathered cartographer who hides her grief behind dry humor.',
  voice:       'Terse, prone to map metaphors, never uses contractions.',
  archetype:   'The Reluctant Sage',
  age:         '32',
  traits:      'curious, stubborn, loyal',
};

// Fields that Claude can suggest via the suggest-field endpoint.
const AI_SUGGESTABLE = new Set(['description', 'voice', 'traits', 'role']);

export default function ChapterBibleDialog({ open, onOpenChange, projectId, onSaved }) {
  const [loading,   setLoading]   = useState(false);
  const [saving,    setSaving]    = useState(false);
  const [chars,     setChars]     = useState([]); // [{id, name, gaps, ...current values, _edits}]
  const [activeIdx, setActiveIdx] = useState(0);

  // Per-field loading key: `${charId}-${field}` → true while Claude is running
  const [suggestingField, setSuggestingField] = useState({});
  // Per-character jump-to-prose loading: charId → true
  const [jumpingTo, setJumpingTo] = useState({});

  // Load gaps when the dialog opens.
  useEffect(() => {
    if (!open || !projectId) return;
    let cancelled = false;
    setLoading(true);
    axios.post(`${API}/projects/${projectId}/character-gaps`,
      { project_id: projectId, extract_if_empty: true },
      { withCredentials: true, timeout: 60000 })
      .then(({ data }) => {
        if (cancelled) return;
        const seeded = (data.characters || []).map((c) => ({
          ...c,
          _edits: {
            role: c.role || '',
            description: c.description || '',
            voice: c.voice || '',
            archetype: c.archetype || '',
            age: c.age || '',
            traits: (c.traits || []).map((t) => t.k).join(', '),
          },
        }));
        setChars(seeded);
        setActiveIdx(0);
      })
      .catch((err) => {
        toast.error(err?.response?.data?.detail || 'Could not load character gaps.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, projectId]);

  const active = chars[activeIdx];
  const totalGaps = useMemo(
    () => chars.reduce((acc, c) => acc + (c.gaps?.length || 0), 0),
    [chars],
  );
  const filledGaps = useMemo(
    () => chars.reduce((acc, c) => acc + ((c.gaps || []).filter((g) => {
      const v = c._edits?.[g];
      if (g === 'traits') return (v || '').trim().split(',').filter(Boolean).length >= 1;
      return (v || '').toString().trim().length > 0;
    }).length), 0),
    [chars],
  );

  const updateField = (field, value) => {
    setChars((prev) => prev.map((c, i) => i === activeIdx
      ? { ...c, _edits: { ...c._edits, [field]: value } }
      : c));
  };

  // ── ✦ Ask Claude — suggest a single field value ────────────────────────
  const suggestField = useCallback(async (charId, field) => {
    const key = `${charId}-${field}`;
    setSuggestingField((prev) => ({ ...prev, [key]: true }));
    try {
      const { data } = await axios.post(
        `${API}/characters/${charId}/suggest-field`,
        { field },
        { withCredentials: true, timeout: 60000 },
      );
      const value = data.value || '';
      setChars((prev) => prev.map((c) =>
        c.id === charId
          ? { ...c, _edits: { ...c._edits, [field]: value } }
          : c,
      ));
      if (data.reasoning) {
        toast.success(data.reasoning, { duration: 4000 });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Could not suggest ${field}.`);
    } finally {
      setSuggestingField((prev) => ({ ...prev, [key]: false }));
    }
  }, []);

  // ── ↗ Find in prose — jump editor to first character mention ──────────
  const findInProse = useCallback(async (char) => {
    if (!projectId || !char?.name) return;
    setJumpingTo((prev) => ({ ...prev, [char.id]: true }));
    try {
      const { data } = await axios.get(
        `${API}/projects/${projectId}/chapters/find-character`,
        { params: { name: char.name }, withCredentials: true },
      );
      if (!data.found) {
        toast.info(`${char.name} doesn't appear in any drafted chapter yet.`);
        return;
      }
      // Switch editor to that chapter first
      window.dispatchEvent(
        new CustomEvent('loom:open-chapter', { detail: { chapterId: data.chapter_id } }),
      );
      // Small delay so the editor panel has time to render the chapter
      setTimeout(() => {
        window.dispatchEvent(
          new CustomEvent('loom:highlight-insertion-point', {
            detail: {
              chapterId:          data.chapter_id,
              afterParagraphText: data.after_paragraph_text || '',
              paragraphIndex:     data.paragraph_index || 0,
              explanation:        `First appearance of ${char.name}`,
            },
          }),
        );
      }, 800);
      toast.success(
        `Jumped to ${char.name}'s first appearance — ${data.chapter_title}.`,
        { duration: 4000 },
      );
      onOpenChange(false); // close the dialog so the editor is visible
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not search chapters.');
    } finally {
      setJumpingTo((prev) => ({ ...prev, [char.id]: false }));
    }
  }, [projectId, onOpenChange]);

  // ── Save all edited characters ─────────────────────────────────────────
  const buildPatch = (c) => {
    const edits = c._edits || {};
    const patch = {};
    if (edits.role && edits.role !== c.role) patch.role = edits.role.slice(0, 60);
    if (edits.description && edits.description !== c.description) patch.description = edits.description.slice(0, 1000);
    if (edits.voice && edits.voice !== c.voice) patch.voice = edits.voice.slice(0, 600);
    if (edits.archetype && edits.archetype !== c.archetype) patch.archetype = edits.archetype.slice(0, 60);
    const age = parseInt(edits.age, 10);
    if (!Number.isNaN(age) && age > 0 && age !== c.age) patch.age = age;

    const originalKeys = (c.traits || []).map((t) => t.k);
    const editedKeys = (edits.traits || '')
      .split(',').map((s) => s.trim()).filter(Boolean)
      .slice(0, 12)
      .map((k) => k.slice(0, 30));
    const keysChanged =
      editedKeys.length !== originalKeys.length ||
      editedKeys.some((k, i) => k !== originalKeys[i]);
    if (keysChanged && editedKeys.length > 0) {
      const byKey = new Map((c.traits || []).map((t) => [t.k, t.v]));
      patch.traits = editedKeys.map((k) => ({
        k,
        v: Number.isFinite(byKey.get(k)) ? byKey.get(k) : 50,
      }));
    }
    return patch;
  };

  const saveAll = async () => {
    setSaving(true);
    let saved = 0;
    try {
      for (const c of chars) {
        const patch = buildPatch(c);
        if (Object.keys(patch).length === 0) continue;
        await charactersApi.update(c.id, patch);
        saved += 1;
      }
      toast.success(`Saved bible updates for ${saved} character${saved === 1 ? '' : 's'}.`);
      window.dispatchEvent(new CustomEvent('loom:character-saved'));
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save character bible.');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="chapter-bible-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BookOpenText className="h-4 w-4 text-primary" /> Chapter Bible — fill the gaps
          </DialogTitle>
          <DialogDescription className="text-[12px]">
            Claude couldn&apos;t infer every character detail from the synopsis.
            Fill the unclear fields below — or let Claude suggest them — so future chapters stay consistent.
          </DialogDescription>
        </DialogHeader>

        {/* Loading / empty / content — overlay covers content while Claude works */}
        <div className="relative min-h-[200px]">
          <AiLoadingOverlay
            visible={loading}
            message="Asking Claude to extract characters from the synopsis…"
          />

          {!loading && chars.length === 0 ? (
            <div className="flex flex-col items-center gap-3 py-12 text-center text-muted-foreground">
              <Sparkles className="h-5 w-5 text-primary" />
              <p className="max-w-sm text-[12px]">
                No characters found. Import a synopsis or add characters in the Character Bible panel first, then re-open this dialog.
              </p>
            </div>
          ) : !loading && (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-[180px_1fr]">
            {/* Left rail: character list */}
            <ul className="max-h-[420px] space-y-1 overflow-y-auto scroll-thin border-r border-panel-border pr-2" data-testid="chapter-bible-char-list">
              {chars.map((c, i) => {
                const gapCount = c.gaps?.length || 0;
                const isActive = i === activeIdx;
                return (
                  <li key={c.id}>
                    <button
                      onClick={() => setActiveIdx(i)}
                      data-testid={`chapter-bible-char-${c.id}`}
                      className={cn(
                        'flex w-full flex-col gap-0.5 rounded-md border px-2 py-1.5 text-left text-[12px] transition-colors',
                        isActive ? 'border-primary bg-primary/10' : 'border-transparent hover:bg-secondary',
                      )}
                    >
                      <div className="flex items-center justify-between gap-1">
                        <span className="truncate font-medium">{c.name}</span>
                        {gapCount === 0
                          ? <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-emerald-500" />
                          : <span className="font-mono-stat text-[10px] text-amber-400">{gapCount} gap{gapCount === 1 ? '' : 's'}</span>}
                      </div>
                    </button>
                    {/* ↗ Find in prose — per character */}
                    {isActive && (
                      <button
                        type="button"
                        onClick={() => findInProse(c)}
                        disabled={jumpingTo[c.id]}
                        className="mt-0.5 flex w-full items-center gap-1 rounded px-2 py-0.5 text-[10.5px] text-primary hover:bg-primary/5 transition disabled:opacity-50"
                        data-testid={`chapter-bible-find-prose-${c.id}`}
                        title="Jump to the character's first appearance in the editor"
                      >
                        {jumpingTo[c.id]
                          ? <Loader2 className="h-3 w-3 animate-spin" />
                          : <ExternalLink className="h-3 w-3" />}
                        Find in prose
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>

            {/* Right pane: form for the active character */}
            <div className="min-w-0 space-y-3" data-testid="chapter-bible-form">
              <div className="flex items-baseline justify-between">
                <h3 className="text-[14px] font-semibold">{active?.name}</h3>
                <span className="font-mono-stat text-[10.5px] text-muted-foreground">
                  Imported {active?.imported_from_synopsis ? 'from synopsis' : 'manually'}
                </span>
              </div>

              {(active?.gaps || []).map((field) => {
                const inputId = `chapter-bible-${active?.id || 'x'}-${field}`;
                const suggestKey = `${active?.id}-${field}`;
                const isLoading = !!suggestingField[suggestKey];
                const canSuggest = AI_SUGGESTABLE.has(field);
                return (
                  <div key={field}>
                    {/* Label row with ✦ Ask Claude button */}
                    <div className="mb-1 flex items-center justify-between gap-2">
                      <Label htmlFor={inputId} className="text-[11px] capitalize">
                        {FIELD_LABELS[field] || field}
                      </Label>
                      {canSuggest && (
                        <button
                          type="button"
                          onClick={() => suggestField(active.id, field)}
                          disabled={isLoading}
                          data-testid={`chapter-bible-suggest-${field}`}
                          className={cn(
                            'flex items-center gap-1 rounded px-1.5 py-0.5 text-[10.5px] font-medium transition',
                            'text-primary hover:bg-primary/8 border border-primary/30 hover:border-primary/60',
                            isLoading && 'opacity-60 cursor-not-allowed',
                          )}
                          title={`Ask Claude to suggest a ${field} for ${active?.name}`}
                        >
                          {isLoading
                            ? <Loader2 className="h-3 w-3 animate-spin" />
                            : <Sparkles className="h-3 w-3" />}
                          {isLoading ? 'Thinking…' : 'Ask Claude'}
                        </button>
                      )}
                    </div>
                    {field === 'description' || field === 'voice' ? (
                      <Textarea
                        id={inputId}
                        rows={field === 'description' ? 3 : 2}
                        value={active._edits[field]}
                        onChange={(e) => updateField(field, e.target.value)}
                        placeholder={FIELD_PLACEHOLDERS[field]}
                        data-testid={`chapter-bible-input-${field}`}
                        className="resize-none text-[12px]"
                      />
                    ) : (
                      <Input
                        id={inputId}
                        type={field === 'age' ? 'number' : 'text'}
                        value={active._edits[field]}
                        onChange={(e) => updateField(field, e.target.value)}
                        placeholder={FIELD_PLACEHOLDERS[field]}
                        data-testid={`chapter-bible-input-${field}`}
                        className="h-8 text-[12px]"
                      />
                    )}
                  </div>
                );
              })}

              {(active?.gaps || []).length === 0 && (
                <div className="rounded-md border border-emerald-500/30 bg-emerald-500/10 p-3 text-[12px] text-emerald-300">
                  <CheckCircle2 className="mr-1 inline h-4 w-4" />
                  {active?.name}&apos;s bible is complete. Nothing left to fill in.
                </div>
              )}
            </div>
          </div>
        )}
        </div>

        <DialogFooter className="items-center justify-between gap-2 sm:justify-between">
          <span className="font-mono-stat text-[10.5px] text-muted-foreground">
            {filledGaps} / {totalGaps} gaps answered
          </span>
          <div className="flex gap-2">
            <Button size="sm" variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}>
              Skip for now
            </Button>
            <Button
              size="sm"
              onClick={saveAll}
              disabled={saving || loading || chars.length === 0}
              data-testid="chapter-bible-save"
              className="gap-1.5"
            >
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <BookOpenText className="h-3.5 w-3.5" />}
              {saving ? 'Saving…' : 'Save & continue'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
